import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import { Paper, Box, FormGroup, FormControlLabel, Checkbox, Typography, Chip, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderEventFilters } from 'models/order-event';
import { PatientOrderId } from 'models/patient';

type PatientHistoryFiltersProps = {
    orderIds: PatientOrderId[];
    onFilterChange: (filters: OrderEventFilters) => void;
    onOrdersChange: (ids: string[]) => void;
};
const PatientHistoryFilters: FC<PatientHistoryFiltersProps> = ({
    orderIds,
    onFilterChange,
    onOrdersChange,
}: PatientHistoryFiltersProps) => {
    const { t: translate } = useTranslation(['order-event', 'orders']);
    const [checkboxes, setCheckboxes] = useState<OrderEventFilters>({
        files: true,
        modified: true,
        comments: true,
        chat: true,
        status: true,
    });
    const [orderCheckboxes, setOrderCheckboxes] = useState<{ [id: string]: boolean }>({});
    const { files, comments, chat, modified, status } = checkboxes;

    useEffect(() => {
        const orderIdMap: { [id: string]: boolean } = {};
        orderIds.map((patientOrderId) => {
            orderIdMap[patientOrderId.id] = true;
        });
        setOrderCheckboxes(orderIdMap);
    }, [orderIds]);

    const handleFilterCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newState = { ...checkboxes, [event.target.name]: event.target.checked };
        setCheckboxes(newState);
        onFilterChange(newState);
    };

    const handleOrderCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newState = { ...orderCheckboxes, [event.target.name]: event.target.checked };
        setOrderCheckboxes(newState);
        onOrdersChange(Object.keys(newState).filter((id) => newState[id]));
    };

    const color = (index: number) => {
        if (index % 4 === 0) {
            return '#9e2b64';
        } else if (index % 4 === 1) {
            return '#1f870c';
        } else if (index % 4 === 2) {
            return '#3e39bf';
        }
        return '#005769';
    };

    return (
        <Paper variant="outlined" square>
            <Box p={1}>
                <Box>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    name="comments"
                                    checked={comments}
                                    onChange={handleFilterCheckboxChange}
                                />
                            }
                            label={translate('SHOW_COMMENTS')}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    name="chat"
                                    checked={chat}
                                    onChange={handleFilterCheckboxChange}
                                />
                            }
                            label={translate('SHOW_CHAT')}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    name="files"
                                    checked={files}
                                    onChange={handleFilterCheckboxChange}
                                />
                            }
                            label={translate('SHOW_ADDED_FILES')}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    name="modified"
                                    checked={modified}
                                    onChange={handleFilterCheckboxChange}
                                />
                            }
                            label={translate('SHOW_ORDER_MODIFIED')}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    name="status"
                                    checked={status}
                                    onChange={handleFilterCheckboxChange}
                                />
                            }
                            label={translate('SHOW_STATUS_CHANGES')}
                        />
                    </FormGroup>
                </Box>
                <Box mt={2}>
                    <Typography variant="h3">{translate('orders:ORDERS')}</Typography>
                    <FormGroup>
                        <Grid container>
                            {orderIds.map((patientOrderId, index) => (
                                <Grid item key={patientOrderId.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="secondary"
                                                name={patientOrderId.id}
                                                checked={orderCheckboxes[patientOrderId.id] || false}
                                                onChange={handleOrderCheckboxChange}
                                            />
                                        }
                                        label={
                                            <Chip
                                                sx={{ color: '#fff', backgroundColor: color(index) }}
                                                label={patientOrderId.orderId}
                                            />
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>
                </Box>
            </Box>
        </Paper>
    );
};

export default PatientHistoryFilters;
